import { formatDate, formatMoney } from '@/utils/format';
import { balanceAfterAdvance } from '@/utils/invoice';
import type { InvoiceAdvance } from '@/types/portal';

interface EarlyPayoutNoticeProps {
  advance: InvoiceAdvance;
  currency: string;
  /** The full invoice value the advance was drawn against. */
  invoiceTotal: number;
  /** 'panel' renders the full callout, 'inline' is a compact chip for lists. */
  variant?: 'panel' | 'inline';
}

/**
 * Payer-side callout: lets a brand see that a creator already drew an early
 * payout against an approved invoice, so only the balance is settled.
 */
export default function EarlyPayoutNotice({
  advance,
  currency,
  invoiceTotal,
  variant = 'panel',
}: EarlyPayoutNoticeProps) {
  const balance = balanceAfterAdvance(invoiceTotal, advance);

  if (variant === 'inline') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-secondary-100 px-1.5 py-0.5 text-[10px] font-label font-medium text-secondary-900">
        <i className="ri-hand-coin-line" />
        Early payout {advance.percent}%
      </span>
    );
  }

  return (
    <div className="rounded-md border border-secondary-200 bg-secondary-50 px-3.5 py-3">
      <p className="flex items-center gap-1.5 text-[11px] font-label font-semibold uppercase tracking-wider text-secondary-900">
        <i className="ri-hand-coin-line" />
        Early payout taken
      </p>
      <p className="mt-1 text-sm leading-relaxed text-secondary-900">
        The creator drew {advance.percent}% of this invoice early —{' '}
        {formatMoney(advance.amount, currency)} advanced on {formatDate(advance.advancedAt)} under{' '}
        {advance.reference}. Pay the remaining balance of {formatMoney(balance, currency)}, not the
        full invoice.
      </p>
    </div>
  );
}